import React, { useState } from "react";

import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { auth, firefuncs } from "config/firebase";
import { httpsCallable } from "firebase/functions";
import { toast } from "react-toastify";

import { createWhiteblacklist } from "_DatabaseFunctions/directConnect/hosts/hostPlayerLists";

function HostRegister() {
  const [loading, setLoading] = useState(false);

  const [values, setValues] = useState({
    accessCode: "",
    responseMsg: "",
    acceptedTerms: false,
  });
  const [showTerms, setShowTerms] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setValues({ 
      ...values, 
      [name]: type === 'checkbox' ? checked : value 
    });
    // Clear error when user starts typing/changing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!values.accessCode.trim()) {
      newErrors.accessCode = 'Access code is required';
    }
    
    if (!values.acceptedTerms) {
      newErrors.acceptedTerms = 'You must accept the Terms of Service to become a host';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('Please sign in to become a host');
        return;
      }
      
      const makeHost = httpsCallable(firefuncs, "addHostRole");
      const res = await makeHost({
        email: user.email,
        accessCode: values.accessCode,
      });
      
      if (res.data.status === "Success") {
        await createWhiteblacklist(user.uid);
        toast.success('Host registration successful!');
        setValues({ accessCode: '', responseMsg: '', acceptedTerms: false });
      } else {
        setValues({ ...values, responseMsg: res.data.message });
      }
    } catch (err) {
      console.error("Host registration error:", err);
      toast.error('An error occurred during host registration');
      setValues({ ...values, responseMsg: 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const termsContent = `
HOST TERMS OF SERVICE

By becoming a host on LocalCards, you agree to the following terms:

1. RESPONSIBILITIES
• You are responsible for ensuring all games are conducted legally and safely
• You must verify that poker games comply with local and state laws
• You agree to maintain a safe and welcoming environment for all players
• You will not discriminate against players based on race, gender, religion, or other protected characteristics

2. GAME MANAGEMENT
• You must accurately represent game details (stakes, location, time, etc.)
• You are responsible for managing game logistics and player communications
• You must handle disputes fairly and professionally
• Games should start reasonably close to the posted time

3. FINANCIAL RESPONSIBILITIES
• You are solely responsible for all money handling and payouts
• LocalCards is not responsible for any financial disputes or losses
• You must maintain accurate records of games and transactions
• Any fees or rake must be clearly disclosed to players

4. PLATFORM USAGE
• You will not use the platform for illegal activities
• You agree to follow all platform guidelines and policies
• Your host privileges may be revoked for violations of these terms
• You will maintain respectful communication with all users

5. LIABILITY
• You assume all liability for games you host
• LocalCards is not liable for injuries, losses, or disputes at your games
• You are responsible for ensuring appropriate insurance coverage
• You indemnify LocalCards against claims arising from your hosted games

6. PRIVACY AND DATA
• You will respect player privacy and personal information
• You will not share player contact information without consent
• You agree to LocalCards' privacy policy regarding data handling

By checking the acceptance box, you acknowledge that you have read, understood, and agree to these terms.
  `;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
        Become a Host
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Host poker games in your area and connect with local players. Please enter your access code and accept our terms to get started.
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
        <TextField
          fullWidth
          label="Access Code"
          name="accessCode"
          value={values.accessCode}
          onChange={handleInputChange}
          error={!!errors.accessCode}
          helperText={errors.accessCode}
          placeholder="Enter your host access code"
          variant="outlined"
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="acceptedTerms"
                checked={values.acceptedTerms}
                onChange={handleInputChange}
                disabled={loading}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I have read and agree to the{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowTerms(true)}
                  sx={{ p: 0, textDecoration: 'underline', textTransform: 'none' }}
                >
                  Host Terms of Service
                </Button>
              </Typography>
            }
          />
          {errors.acceptedTerms && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              {errors.acceptedTerms}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {loading ? (
            <CircularProgress color="primary" />
          ) : (
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ minWidth: 200 }}
            >
              Submit Host Request
            </Button>
          )}
        </Box>

        {values.responseMsg && (
          <Alert 
            severity={values.responseMsg.toLowerCase().includes('success') ? 'success' : 'error'}
            sx={{ mt: 2 }}
          >
            {values.responseMsg}
          </Alert>
        )}
      </Box>

      {/* Terms of Service Dialog */}
      <Dialog
        open={showTerms}
        onClose={() => setShowTerms(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Typography variant="h5" component="div" align="center">
            Host Terms of Service
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
            {termsContent}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTerms(false)} variant="outlined">
            Close
          </Button>
          <Button 
            onClick={() => {
              setShowTerms(false);
              setValues({ ...values, acceptedTerms: true });
            }} 
            variant="contained"
          >
            Accept Terms
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HostRegister;
